import { FC, useEffect, useState } from "react";
import { Animated, Easing, View } from "react-native";

import { Card } from "./base/card";
import { HStack } from "./base/hstack";

interface TypingIndicatorProps {
  isTyping: boolean;
}

const DOT_COLOR = "rgb(115, 115, 115)";

const Dot: FC<{ opacity: Animated.Value }> = ({ opacity }) => (
  <View className="rounded-full h-2 w-2 overflow-hidden">
    <Animated.View
      style={{
        backgroundColor: DOT_COLOR,
        borderRadius: 9999,
        height: 8,
        width: 8,
        opacity,
      }}
    />
  </View>
);

const TypingIndicator: FC<TypingIndicatorProps> = ({ isTyping }) => {
  const [dot1Opacity] = useState(() => new Animated.Value(0.3));
  const [dot2Opacity] = useState(() => new Animated.Value(0.3));
  const [dot3Opacity] = useState(() => new Animated.Value(0.3));

  useEffect(() => {
    if (isTyping) {
      const createAnimation = (
        animatedValue: Animated.Value,
        delay: number
      ) => {
        return Animated.loop(
          Animated.sequence([
            Animated.delay(delay),
            Animated.timing(animatedValue, {
              duration: 600,
              easing: Easing.inOut(Easing.ease),
              toValue: 1,
              useNativeDriver: true,
            }),
            Animated.timing(animatedValue, {
              duration: 600,
              easing: Easing.inOut(Easing.ease),
              toValue: 0.3,
              useNativeDriver: true,
            }),
          ])
        );
      };

      const animation1 = createAnimation(dot1Opacity, 0);
      const animation2 = createAnimation(dot2Opacity, 200);
      const animation3 = createAnimation(dot3Opacity, 400);

      animation1.start();
      animation2.start();
      animation3.start();

      return () => {
        animation1.stop();
        animation2.stop();
        animation3.stop();
      };
    } else {
      dot1Opacity.setValue(0.3);
      dot2Opacity.setValue(0.3);
      dot3Opacity.setValue(0.3);
    }
  }, [isTyping, dot1Opacity, dot2Opacity, dot3Opacity]);

  if (!isTyping) {
    return null;
  }

  return (
    <HStack className="justify-start mb-4">
      <Card className="max-w-[85%] p-4 bg-white dark:bg-outline-800 border border-outline-200 dark:border-outline-700 rounded-2xl rounded-tl-sm">
        <HStack className="gap-1 items-center">
          <Dot opacity={dot1Opacity} />
          <Dot opacity={dot2Opacity} />
          <Dot opacity={dot3Opacity} />
        </HStack>
      </Card>
    </HStack>
  );
};

export default TypingIndicator;
